import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AcceptOrderOptions,
  AcceptOrderResponse,
  CancelOrderOptions,
  CancelOrderResponse,
  FulfillOrderResponse,
  GetOrderOptions,
  ListOrdersOptions,
  ListOrdersResponse,
  Order,
  OrderAcceptedEnvelope,
  OrderCanceledEnvelope,
  OrderCreatedEnvelope,
  OrderFulfilledEnvelope,
  OrderNewOrderEnvelope,
  acceptOrder as universalAcceptOrder,
  cancelOrder as universalCancelOrder,
  fulfillOrder as universalFulfillOrder,
  getOrder as universalGetOrder,
  listOrders as universalListOrders,
} from './restaurants-v3-order-orders.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function getOrder(httpClient: HttpClient): GetOrderSignature {
  return (_id: string, options?: GetOrderOptions) =>
    universalGetOrder(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetOrderSignature {
  /**
   * TEST The `getOrder()` function returns a Promise that is returned when an order is retrieved.
   *
   * 1. First do this.
   * 1. The do that
   *     + This is a bullet
   *     + So is this.
   *
   * > **Note**: This indentation is not how our notes generally look but I will waiit to see the branch preview. I am just trying out some markdown here.
   *
   * @param - ID of the order to retrieve.
   * @returns TEST Curious where this shows up.
   * @deprecated
   */
  (_id: string, options?: GetOrderOptions): Promise<
    NonNullablePaths<
      Order,
      | `_id`
      | `status`
      | `lineItems`
      | `lineItems.${number}.quantity`
      | `lineItems.${number}.price`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `discounts`
      | `discounts.${number}.catalogDiscountId`
      | `discounts.${number}.appliedAmount`
      | `discounts.${number}.catalogDiscountType`
      | `discounts.${number}.catalogDiscountName`
      | `discounts.${number}.catalogDiscountDescription`
      | `coupon.code`
      | `coupon._id`
      | `payments`
      | `payments.${number}.type`
      | `fulfillment.deliveryDetails.externalProvider._id`
      | `fulfillment.deliveryDetails.externalProvider.name`
      | `fulfillment.deliveryDetails.address.onArrival`
      | `fulfillment.pickupDetails.curbside.info`
      | `fulfillment.dineInDetails.label`
      | `fulfillment.dineInDetails.value`
      | `fulfillment.type`
      | `customer.firstName`
      | `customer.lastName`
      | `customer.email`
      | `totals.subtotal`
      | `totals.total`
      | `totals.quantity`
      | `activities`
      | `channelInfo.type`,
      5
    >
  >;
}

export function listOrders(httpClient: HttpClient): ListOrdersSignature {
  return (options?: ListOrdersOptions) =>
    universalListOrders(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListOrdersSignature {
  /**
   * Retrieves a list of up to 1000 orders, given the provided filters.
   * @deprecated
   */
  (options?: ListOrdersOptions): Promise<
    NonNullablePaths<
      ListOrdersResponse,
      | `orders`
      | `orders.${number}._id`
      | `orders.${number}.status`
      | `orders.${number}.coupon.code`
      | `orders.${number}.coupon._id`
      | `orders.${number}.fulfillment.dineInDetails.label`
      | `orders.${number}.fulfillment.dineInDetails.value`
      | `orders.${number}.fulfillment.type`
      | `orders.${number}.customer.firstName`
      | `orders.${number}.customer.lastName`
      | `orders.${number}.customer.email`
      | `orders.${number}.totals.subtotal`
      | `orders.${number}.totals.total`
      | `orders.${number}.totals.quantity`
      | `orders.${number}.channelInfo.type`,
      6
    >
  >;
}

export function acceptOrder(httpClient: HttpClient): AcceptOrderSignature {
  return (_id: string, options?: AcceptOrderOptions) =>
    universalAcceptOrder(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AcceptOrderSignature {
  /**
   * Changes the order status to `ACCEPTED`.
   *
   * > **Note:** It is not possible to change the order status from `FULFILLED` to `ACCEPTED`.
   * @param - ID of the order to mark as `ACCEPTED`.
   * @deprecated
   */
  (_id: string, options?: AcceptOrderOptions): Promise<
    NonNullablePaths<
      AcceptOrderResponse,
      | `order._id`
      | `order.status`
      | `order.lineItems`
      | `order.lineItems.${number}.quantity`
      | `order.lineItems.${number}.price`
      | `order.lineItems.${number}.catalogReference.catalogItemId`
      | `order.discounts`
      | `order.discounts.${number}.catalogDiscountId`
      | `order.discounts.${number}.appliedAmount`
      | `order.discounts.${number}.catalogDiscountType`
      | `order.discounts.${number}.catalogDiscountName`
      | `order.discounts.${number}.catalogDiscountDescription`
      | `order.coupon.code`
      | `order.coupon._id`
      | `order.payments`
      | `order.payments.${number}.type`
      | `order.fulfillment.deliveryDetails.externalProvider._id`
      | `order.fulfillment.deliveryDetails.externalProvider.name`
      | `order.fulfillment.deliveryDetails.address.onArrival`
      | `order.fulfillment.pickupDetails.curbside.info`
      | `order.fulfillment.dineInDetails.label`
      | `order.fulfillment.dineInDetails.value`
      | `order.fulfillment.type`
      | `order.customer.firstName`
      | `order.customer.lastName`
      | `order.customer.email`
      | `order.totals.subtotal`
      | `order.totals.total`
      | `order.totals.quantity`
      | `order.activities`
      | `order.channelInfo.type`,
      6
    >
  >;
}

export function cancelOrder(httpClient: HttpClient): CancelOrderSignature {
  return (_id: string, options?: CancelOrderOptions) =>
    universalCancelOrder(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CancelOrderSignature {
  /**
   * Changes the order status to `CANCELED`.
   * @param - ID of the order to mark as `CANCELED`.
   * @deprecated
   */
  (_id: string, options?: CancelOrderOptions): Promise<
    NonNullablePaths<
      CancelOrderResponse,
      | `order._id`
      | `order.status`
      | `order.lineItems`
      | `order.lineItems.${number}.quantity`
      | `order.lineItems.${number}.price`
      | `order.lineItems.${number}.catalogReference.catalogItemId`
      | `order.discounts`
      | `order.discounts.${number}.catalogDiscountId`
      | `order.discounts.${number}.appliedAmount`
      | `order.discounts.${number}.catalogDiscountType`
      | `order.discounts.${number}.catalogDiscountName`
      | `order.discounts.${number}.catalogDiscountDescription`
      | `order.coupon.code`
      | `order.coupon._id`
      | `order.payments`
      | `order.payments.${number}.type`
      | `order.fulfillment.deliveryDetails.externalProvider._id`
      | `order.fulfillment.deliveryDetails.externalProvider.name`
      | `order.fulfillment.deliveryDetails.address.onArrival`
      | `order.fulfillment.pickupDetails.curbside.info`
      | `order.fulfillment.dineInDetails.label`
      | `order.fulfillment.dineInDetails.value`
      | `order.fulfillment.type`
      | `order.customer.firstName`
      | `order.customer.lastName`
      | `order.customer.email`
      | `order.totals.subtotal`
      | `order.totals.total`
      | `order.totals.quantity`
      | `order.activities`
      | `order.channelInfo.type`,
      6
    >
  >;
}

export function fulfillOrder(httpClient: HttpClient): FulfillOrderSignature {
  return (_id: string) =>
    universalFulfillOrder(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface FulfillOrderSignature {
  /**
   * Changes the order status to `FULFILLED`.
   * @param - ID of the order to mark as `FULFILLED`.
   * @deprecated
   */
  (_id: string): Promise<
    NonNullablePaths<
      FulfillOrderResponse,
      | `order._id`
      | `order.status`
      | `order.lineItems`
      | `order.lineItems.${number}.quantity`
      | `order.lineItems.${number}.price`
      | `order.lineItems.${number}.catalogReference.catalogItemId`
      | `order.discounts`
      | `order.discounts.${number}.catalogDiscountId`
      | `order.discounts.${number}.appliedAmount`
      | `order.discounts.${number}.catalogDiscountType`
      | `order.discounts.${number}.catalogDiscountName`
      | `order.discounts.${number}.catalogDiscountDescription`
      | `order.coupon.code`
      | `order.coupon._id`
      | `order.payments`
      | `order.payments.${number}.type`
      | `order.fulfillment.deliveryDetails.externalProvider._id`
      | `order.fulfillment.deliveryDetails.externalProvider.name`
      | `order.fulfillment.deliveryDetails.address.onArrival`
      | `order.fulfillment.pickupDetails.curbside.info`
      | `order.fulfillment.dineInDetails.label`
      | `order.fulfillment.dineInDetails.value`
      | `order.fulfillment.type`
      | `order.customer.firstName`
      | `order.customer.lastName`
      | `order.customer.email`
      | `order.totals.subtotal`
      | `order.totals.total`
      | `order.totals.quantity`
      | `order.activities`
      | `order.channelInfo.type`,
      6
    >
  >;
}

export const onOrderAccepted = EventDefinition(
  'wix.restaurants.v3.order_accepted',
  true,
  (event: OrderAcceptedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.fulfillment.promisedTime' },
            {
              path: 'data.order.fulfillment.deliveryDetails.externalProvider.pickupTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.externalProvider.dropOffTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.pickupInfo.windowStartTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.pickupInfo.windowEndTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.dropOffInfo.windowStartTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.dropOffInfo.windowEndTime',
            },
            { path: 'data.order.activities.timestamp' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'data.order.fulfillment.deliveryDetails.address.location.latitude',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.address.location.longitude',
            },
          ],
        },
      ])
    )
)<OrderAcceptedEnvelope>();
export const onOrderCanceled = EventDefinition(
  'wix.restaurants.v3.order_canceled',
  true,
  (event: OrderCanceledEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.fulfillment.promisedTime' },
            {
              path: 'data.order.fulfillment.deliveryDetails.externalProvider.pickupTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.externalProvider.dropOffTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.pickupInfo.windowStartTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.pickupInfo.windowEndTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.dropOffInfo.windowStartTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.dropOffInfo.windowEndTime',
            },
            { path: 'data.order.activities.timestamp' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'data.order.fulfillment.deliveryDetails.address.location.latitude',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.address.location.longitude',
            },
          ],
        },
      ])
    )
)<OrderCanceledEnvelope>();
export const onOrderCreated = EventDefinition(
  'wix.restaurants.v3.order_created',
  true,
  (event: OrderCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.fulfillment.promisedTime' },
            {
              path: 'entity.fulfillment.deliveryDetails.externalProvider.pickupTime',
            },
            {
              path: 'entity.fulfillment.deliveryDetails.externalProvider.dropOffTime',
            },
            {
              path: 'entity.fulfillment.deliveryDetails.pickupInfo.windowStartTime',
            },
            {
              path: 'entity.fulfillment.deliveryDetails.pickupInfo.windowEndTime',
            },
            {
              path: 'entity.fulfillment.deliveryDetails.dropOffInfo.windowStartTime',
            },
            {
              path: 'entity.fulfillment.deliveryDetails.dropOffInfo.windowEndTime',
            },
            { path: 'entity.activities.timestamp' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.fulfillment.deliveryDetails.address.location.latitude',
            },
            {
              path: 'entity.fulfillment.deliveryDetails.address.location.longitude',
            },
          ],
        },
      ])
    )
)<OrderCreatedEnvelope>();
export const onOrderFulfilled = EventDefinition(
  'wix.restaurants.v3.order_fulfilled',
  true,
  (event: OrderFulfilledEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.fulfillment.promisedTime' },
            {
              path: 'data.order.fulfillment.deliveryDetails.externalProvider.pickupTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.externalProvider.dropOffTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.pickupInfo.windowStartTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.pickupInfo.windowEndTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.dropOffInfo.windowStartTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.dropOffInfo.windowEndTime',
            },
            { path: 'data.order.activities.timestamp' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'data.order.fulfillment.deliveryDetails.address.location.latitude',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.address.location.longitude',
            },
          ],
        },
      ])
    )
)<OrderFulfilledEnvelope>();
export const onOrderNewOrder = EventDefinition(
  'wix.restaurants.v3.order_new_order',
  true,
  (event: OrderNewOrderEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.fulfillment.promisedTime' },
            {
              path: 'data.order.fulfillment.deliveryDetails.externalProvider.pickupTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.externalProvider.dropOffTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.pickupInfo.windowStartTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.pickupInfo.windowEndTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.dropOffInfo.windowStartTime',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.dropOffInfo.windowEndTime',
            },
            { path: 'data.order.activities.timestamp' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'data.order.fulfillment.deliveryDetails.address.location.latitude',
            },
            {
              path: 'data.order.fulfillment.deliveryDetails.address.location.longitude',
            },
          ],
        },
      ])
    )
)<OrderNewOrderEnvelope>();

export {
  AcceptOrderOptions,
  AcceptOrderOptionsDeliveryTimeOneOf,
  AcceptOrderRequest,
  AcceptOrderRequestDeliveryTimeOneOf,
  AcceptOrderResponse,
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Activity,
  BaseEventMetadata,
  CancelOrderOptions,
  CancelOrderRequest,
  CancelOrderResponse,
  CatalogReference,
  ChannelInfo,
  CountOrdersQuery,
  CountOrdersRequest,
  CountOrdersResponse,
  Coupon,
  Curbside,
  CursorPaging,
  Cursors,
  Customer,
  CustomerIdOneOf,
  DateRangeFilter,
  DeliveryAddress,
  DeliveryAddressLocation,
  DeliveryDetails,
  DeliveryDetailsProviderOneOf,
  DineInDetails,
  Discount,
  DiscountType,
  DisplayType,
  DomainEvent,
  DomainEventBodyOneOf,
  DropOffInfo,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Error,
  EventMetadata,
  EventType,
  ExternalProvider,
  FieldName,
  FulfillOrderRequest,
  FulfillOrderResponse,
  Fulfillment,
  FulfillmentDetailsOneOf,
  FulfillmentType,
  GetOrderByMsidRequest,
  GetOrderByMsidResponse,
  GetOrderOptions,
  GetOrderRequest,
  GetOrderResponse,
  GetTotalActiveOrdersRequest,
  GetTotalActiveOrdersResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  LineItem,
  LineItemOption,
  LineItemOptionItem,
  ListOrdersByMsidRequest,
  ListOrdersByMsidResponse,
  ListOrdersOptions,
  ListOrdersRequest,
  ListOrdersResponse,
  LocationToAmount,
  LoyaltyInfo,
  MessageEnvelope,
  Money,
  NewOrder,
  NotifyOrderEventRequest,
  NotifyOrderEventResponse,
  OnArrival,
  Order,
  OrderAccepted,
  OrderAcceptedEnvelope,
  OrderCanceled,
  OrderCanceledEnvelope,
  OrderCreatedEnvelope,
  OrderFulfilled,
  OrderFulfilledEnvelope,
  OrderNewOrderEnvelope,
  PagingMetadataV2,
  Payment,
  PaymentType,
  PickupDetails,
  PickupInfo,
  PosInfo,
  PosInfoExternalProvider,
  Restaurant,
  RestoreInfo,
  ServiceFee,
  Sort,
  SortOrder,
  Status,
  Totals,
  Type,
  WebhookIdentityType,
} from './restaurants-v3-order-orders.universal.js';
